import React from 'react';
import BackHeader from '../components/BackHeader';
import useTranslation from '../hooks/useTranslation';

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();
  
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="bg-white min-h-screen flex flex-col">
      <BackHeader title={t('Privacy Policy')} />
      
      <div className="p-4 pb-20 flex-1 overflow-y-auto">
        <div className="mb-6 text-left">
          <p className="text-gray-500"><strong>Effective Date:</strong> August 1, 2025</p>
          <p className="text-gray-500"><strong>Company:</strong> ORDER MADE EASY</p>
          <p className="text-gray-500"><strong>Representative:</strong> Dongjin Lee</p>
          <p className="text-gray-500"><strong>Address:</strong> 24-11, Baekjegobun-ro 40-gil, Songpa-gu, Seoul</p>
          <p className="text-gray-500"><strong>Email:</strong> koreaorder.official@gmail.com</p>
        </div>
        
        <section className="mb-6 text-left">
          <p className="text-gray-700 leading-relaxed mb-4">
            ORDER MADE EASY (hereinafter referred to as the "Company") values your personal information and strictly complies with applicable Korean laws, including the Personal Information Protection Act. This Privacy Policy explains how we collect, use, disclose, retain, and protect your personal information.
          </p>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">1. Personal Information We Collect</h3>
          <p className="text-gray-700 leading-relaxed mb-2">We collect the following types of personal information:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Provided by the user: delivery address, payment information, device information, email address</li>
            <li>Automatically collected: IP address, browser type, device type, access date and time</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">2. Purpose of Collection and Use</h3>
          <p className="text-gray-700 leading-relaxed mb-2">We use the collected personal information for the following purposes:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>To process and deliver orders</li>
            <li>To provide customer service and respond to inquiries</li>
            <li>To process payments and manage transactions</li>
            <li>To analyze service usage and improve user experience</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">3. Retention and Use Period</h3>
          <p className="text-gray-700 leading-relaxed mb-2">We retain personal information only for the period necessary to achieve the purposes outlined above:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Default retention period: <strong>3 years after service termination</strong></li>
            <li>We securely delete or anonymize the data thereafter, unless otherwise required by law</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">4. Provision of Personal Information to Third Parties</h3>
          <p className="text-gray-700 leading-relaxed mb-2">We do not share your personal information with third parties unless required by law or consented by you. However, the following is an exception:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Recipient: <strong>Eximbay (PG Company)</strong></li>
            <li>Information Provided: Payment-related information</li>
            <li>Purpose: Secure payment processing and settlement</li>
            <li>Retention Period: According to legal and internal retention standards</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">5. Outsourcing of Personal Information Processing</h3>
          <p className="text-gray-700 leading-relaxed mb-2">To provide better service, we outsource certain personal information processing tasks:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Contractor: <strong>Eximbay</strong></li>
            <li>Task: Payment processing and settlement services</li>
            <li>Retention Period: Until the end of the service contract or required retention period</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">6. Destruction of Personal Information</h3>
          <p className="text-gray-700 leading-relaxed mb-2">We destroy personal information without delay once its purpose has been fulfilled:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Electronic files: Irreversibly deleted using secure methods</li>
            <li>Printed materials: Shredded or incinerated</li>
            <li>Destruction logs are recorded and managed securely</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">7. User Rights and How to Exercise Them</h3>
          <p className="text-gray-700 leading-relaxed mb-2">You have the right to access, modify, delete, or request suspension of your personal information at any time.</p>
          <p className="text-gray-700 leading-relaxed mb-2">Please contact us via the email or phone number below:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Email: koreaorder.official@gmail.com</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">8. Protection of Children's Personal Information</h3>
          <p className="text-gray-700 leading-relaxed">
            Our services are not intended for children under the age of 14. We do not knowingly collect personal data from minors. If such collection is discovered, we will delete the information without delay.
          </p>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">9. Use of Personal Information for Marketing</h3>
          <p className="text-gray-700 leading-relaxed mb-2">With your prior consent, we may use your personal information for the following:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Promotional emails, discounts, coupon notifications, or event alerts</li>
            <li>You may opt-out at any time via email or customer service</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">10. Use of Cookies</h3>
          <p className="text-gray-700 leading-relaxed">
            We may use cookies to enhance service quality and improve user experience. You may refuse cookies through your browser settings.
          </p>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">11. Processing of Personal Information for Overseas Users</h3>
          <p className="text-gray-700 leading-relaxed mb-2">
            This service is operated in the Republic of Korea, and all data is managed in accordance with Korean laws.
          </p>
          <p className="text-gray-700 leading-relaxed">
            By using our service, <strong>you agree that your personal information may be processed on Korean servers or via domestic service providers</strong> (e.g., PG companies).
          </p>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <h3 className="text-lg font-semibold mb-2">12. Personal Information Protection Officer</h3>
          <p className="text-gray-700 leading-relaxed mb-2">For inquiries regarding personal information protection:</p>
          <ul className="list-disc pl-5 mb-2 text-gray-700">
            <li>Officer Name: <strong>Dongjin Lee</strong></li>
            <li>Email: korearorder.official@gmail.com</li>
          </ul>
        </section>

        <hr className="mb-6 border-gray-200" />

        <section className="mb-6 text-left">
          <p className="text-gray-700 leading-relaxed">
          This Privacy Policy is effective as of <strong>August 1, 2025</strong>, and any updates will be posted in advance on our website or service screen.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
